import { SelectChangeEvent, Stack } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Account,
  Currency,
  Direction,
  GridType,
  RangeStart,
  Ticker,
  TradingViewLaunchProps,
} from "../../_app/types";
import Box from "@mui/material/Box";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  setBase,
  setCurrencyType,
  setCustomHighPrice,
  setCustomLowPrice,
  setDirection,
  setGridsAmount,
  setGridType,
  setInvestment,
  setLeverage,
  setMaxPrice,
  setMinPrice,
  setPriceStep,
  setStartPrice,
  setStepsCount,
  setTicker,
} from "../../store/slices/botSlice";
import { Order } from "../../class/Order";
import { _roundDec } from "../../utils/_roundToDecimals";
import { useIsMount } from "../../hooks/use-in-mount";
import { getLastEntityLeverage } from "../../utils/async";
import { ChartingLibraryWidgetOptions, LoadingScreenOptions } from "../../charting_library";
import BybitAPI from "../../class/bybit/bybitApi";
import { useTheme } from "@mui/material/styles";
import binanceAPI from "../../class/binanceApi";
import { toggleChart } from "../../store/slices/appSettingsSlice";
import CollapseChildren from "../../_components/CollapseChildren";
import Header from "../../_modules/Header/Header";
import PageLayout from "../../_components/PageLayout";
import Spacer from "../../_components/Spacer";
import { useAppNavigate } from "../../_hooks/use-app-navigate";
import { APP_ID } from "../../_app/consts";
import TickerSelect from "../../components/AddingBot/TickerSelect";
import { ButtonStyled } from "../../_components/ButtonStyled";
import { TradingView } from "../../components/ui/TradingView";
import AdvancedGridSettings from "../../components/AddingBot/AdvancedGridSettings";
import BotPreviewPage from "../../components/AddingBot/BotPreviewPage";
import { useApi } from "../../_hooks/use-api";
import FuturesBotRangeSettings from "./components/FuturesBotRangeSettings";
import FuturesBotPagination from "./components/FuturesBotPagination";
import FuturesBotGridSettings from "./components/FuturesBotGridSettings";
import FuturesBotInvestmentSettings from "./components/FuturesBotInvestmentSettings";
import FuturesBotPreviewPage from "./components/FuturesBotPreviewPage";
import InsuranceSettings from "../../_modules/InsuranceSettings/InsuranceSettings";
import Bot from "../../components/ui/icons/Bot";
import { formatCurrency } from "../../utils/utils";
import formatNumber from "../../_utils/formatNumber";

interface Props {
  ticker?: string;
  tickerPrice: number;
  accounts: Account[];
  tickers: Ticker[];
}

const FuturesBotCreationPage = ({ tickerPrice, tickers }: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useAppNavigate();
  const isMount = useIsMount();
  const api = useApi();

  const CustomGridMode = useSelector((state: RootState) => state.customGridMode);
  const exchange = useSelector((state: RootState) => state.user.activeAccount.market);
  const activeAccountID = useSelector((state: RootState) => state.user.activeAccount.id);
  const profileId = useSelector((state: RootState) => state.user.profile.profileId);

  function findTickerByBase(tickers: Ticker[], base: string): Ticker {
    return tickers.find((ticker) => ticker.base === base) as Ticker;
  }
  const [step, setStep] = useState<number>(1);

  const handleStepForm = (value: number) => {
    if (value === 2 && step === 1) {
      // save user range only first step form
      dispatch(setCustomLowPrice(newBotRedux.minPrice));
      dispatch(setCustomHighPrice(newBotRedux.maxPrice));
    }
    setStep(value);
  };

  const [investmentStepInBase, setInvestmentStepInBase] = useState<number>(0);
  const [investmentStepInQuote, setInvestmentStepInQuote] = useState<number>(0);
  const newBotRedux = useSelector((state: RootState) => state.bot);
  const [orderList, setOrderList] = useState<Order[]>([]);
  const [rangeStartMode, setRangeStartMode] = useState<RangeStart>("range");
  const [isEditing, setIsEditing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);

  const showChart = useSelector((state: RootState) => state.appSettings.showChart);
  const ChartOptions: TradingViewLaunchProps = useMemo(() => {
    return {
      locale: "en",
      debug: false,
      symbol: `${newBotRedux.ticker.base}${newBotRedux.ticker.quote}`,
      interval: "15" as ChartingLibraryWidgetOptions["interval"],
      theme: theme.palette.mode === "dark" ? "dark" : "light",
      timezone: Intl.DateTimeFormat().resolvedOptions()
        .timeZone as ChartingLibraryWidgetOptions["timezone"],
      autosize: true,
      datafeed:
        exchange === "BINANCE"
          ? new binanceAPI({
              debug: false,
              ticker: `${newBotRedux.ticker.base}${newBotRedux.ticker.quote}`,
            })
          : new BybitAPI({
              debug: false,
              ticker: `${newBotRedux.ticker.base}${newBotRedux.ticker.quote}`,
            }),
      loading_screen: {
        backgroundColor:
          theme.palette.mode === "dark" ? theme.palette.grey[500] : theme.palette.grey[300],
      } as LoadingScreenOptions,
    };
  }, [newBotRedux.ticker, exchange]);

  const handleChartView = () => {
    dispatch(toggleChart(!showChart));
    localStorage.setItem("viewChart" + APP_ID, String(!showChart));
  };

  const handleOrderChange = (value: Order[]) => setOrderList(value);
  // const baseCurrencyList = ['USDT', 'BTC', 'ETH', 'USDC'];
  const handlePriceStep = (value: number) => dispatch(setPriceStep(value));
  const handleDirection = (value: Direction) => dispatch(setDirection(value));
  const handleGridType = (value: GridType) => dispatch(setGridType(value));
  const handleGridsAmount = (value: number) => dispatch(setGridsAmount(value));
  const handleStartPrice = (value: number) => dispatch(setStartPrice(value));
  const handleCurrencyType = (value: Currency) => dispatch(setCurrencyType(value));
  const handleInvestment = (value: number) => dispatch(setInvestment(value));
  const handleLeverage = (value: number) => dispatch(setLeverage(value));
  const handleMinPrice = (value: number) => dispatch(setMinPrice(value));
  const handleMaxPrice = (value: number) => dispatch(setMaxPrice(value));
  const handleStepsCount = (value: number) => dispatch(setStepsCount(value));
  const handleBase = (event: SelectChangeEvent<any>) => {
    event.preventDefault();
    dispatch(setTicker(findTickerByBase(tickers, event.target.value)));
    dispatch(setBase(event.target.value));
  };

  const handleInvestmentInBase = (value: number) => setInvestmentStepInBase(value);
  const handleInvestmentInQuote = (value: number) => setInvestmentStepInQuote(value);

  const [showPreview, setShowPreview] = useState<boolean>(false);

  useEffect(() => {
    if (!CustomGridMode) {
      dispatch(setPriceStep(newBotRedux.gridType === "ARITHMETIC" ? tickerPrice * 0.01 : 0.01)); // предзаполняем priceStep 1% от стоимости ticker
    }
  }, [tickerPrice]);

  useEffect(() => {
    if (!CustomGridMode) {
      let steps = 0;
      if (newBotRedux.currencyType === "BASE") {
        steps = _roundDec(newBotRedux.investment, 4) / investmentStepInBase;
      } else {
        steps = _roundDec(newBotRedux.investment, 2) / investmentStepInQuote;
      }
      dispatch(setStepsCount(_roundDec(steps, 0)));
    }
  }, [newBotRedux.investment]);

  useEffect(() => {
    if (!profileId) return;
    if (!activeAccountID) return;

    const fetchLeverage = async () => {
      try {
        const result = await getLastEntityLeverage(api, activeAccountID, profileId, "bots");
        if (result) {
          dispatch(setLeverage(result as number));
        } else {
          dispatch(setLeverage(1));
        }
      } catch (error) {
        dispatch(setLeverage(1));
      }
    };
    fetchLeverage();
  }, [activeAccountID, profileId]);

  useEffect(() => {
    if (!isMount) {
      if (rangeStartMode === "range") {
        if (newBotRedux.gridType === "ARITHMETIC") {
          switch (newBotRedux.gridDirection) {
            case "Central":
              if (
                newBotRedux.minPrice > 0 &&
                newBotRedux.maxPrice > 0 &&
                newBotRedux.priceStep > 0
              ) {
                if (newBotRedux.minPrice < newBotRedux.maxPrice) {
                  let startPrice =
                    newBotRedux.minPrice + (newBotRedux.gridsAmount / 2) * newBotRedux.priceStep;
                  handleStartPrice(startPrice);
                } else if (newBotRedux.minPrice > newBotRedux.maxPrice) {
                  let startPrice =
                    newBotRedux.maxPrice + (newBotRedux.gridsAmount / 2) * newBotRedux.priceStep;
                  handleStartPrice(startPrice);
                }
              }
              break;
            case "Long":
              handleStartPrice(newBotRedux.maxPrice);
              break;
            case "Short":
              handleStartPrice(newBotRedux.minPrice);
              break;
            default:
              break;
          }
        } else if (newBotRedux.gridType === "GEOMETRIC") {
          switch (newBotRedux.gridDirection) {
            case "Central":
              if (
                newBotRedux.minPrice > 0 &&
                newBotRedux.maxPrice > 0 &&
                newBotRedux.priceStep > 0
              ) {
                if (newBotRedux.minPrice < newBotRedux.maxPrice) {
                  let startPrice =
                    newBotRedux.minPrice *
                    Math.pow(1 + newBotRedux.priceStep, newBotRedux.gridsAmount / 2);
                  handleStartPrice(startPrice);
                } else if (newBotRedux.minPrice > newBotRedux.maxPrice) {
                  let startPrice =
                    newBotRedux.maxPrice *
                    Math.pow(1 + newBotRedux.priceStep, newBotRedux.gridsAmount / 2);
                  handleStartPrice(startPrice);
                }
              }
              break;
            case "Long":
              handleStartPrice(newBotRedux.maxPrice);
              break;
            case "Short":
              handleStartPrice(newBotRedux.minPrice);
              break;
            default:
              break;
          }
        }
      } else if (rangeStartMode === "start") {
        if (newBotRedux.gridType === "ARITHMETIC") {
          switch (newBotRedux.gridDirection) {
            case "Central":
              handleMinPrice(
                newBotRedux.startPrice - (newBotRedux.gridsAmount / 2) * newBotRedux.priceStep
              );
              handleMaxPrice(
                newBotRedux.startPrice + (newBotRedux.gridsAmount / 2) * newBotRedux.priceStep
              );
              break;
            case "Long":
              handleMinPrice(
                newBotRedux.startPrice - newBotRedux.gridsAmount * newBotRedux.priceStep
              );
              handleMaxPrice(newBotRedux.startPrice);
              break;
            case "Short":
              handleMinPrice(newBotRedux.startPrice);
              handleMaxPrice(
                newBotRedux.startPrice + newBotRedux.gridsAmount * newBotRedux.priceStep
              );
              break;
            default:
              break;
          }
        } else if (newBotRedux.gridType === "GEOMETRIC") {
          switch (newBotRedux.gridDirection) {
            case "Central":
              handleMinPrice(
                newBotRedux.startPrice /
                  Math.pow(1 + newBotRedux.priceStep, newBotRedux.gridsAmount / 2)
              );
              handleMaxPrice(
                newBotRedux.startPrice /
                  Math.pow(1 - newBotRedux.priceStep, newBotRedux.gridsAmount / 2)
              );
              break;
            case "Long":
              handleMinPrice(
                newBotRedux.startPrice *
                  Math.pow(1 + newBotRedux.priceStep, newBotRedux.gridsAmount)
              );
              handleMaxPrice(newBotRedux.startPrice);
              break;
            case "Short":
              handleMinPrice(newBotRedux.startPrice);
              handleMaxPrice(
                newBotRedux.startPrice /
                  Math.pow(1 - newBotRedux.priceStep, newBotRedux.gridsAmount)
              );
              break;
            default:
              break;
          }
        }
      }
    }
  }, [newBotRedux.gridDirection]);

  return (
    <PageLayout
      contentSx={{ padding: "20px 8px 0" }}
      header={
        <Header
          pageTitle={"Bot creation"}
          needBack
          onChange={() => {
            if (step === 1) {
              navigate.back();
            }
            setStep((prev) => prev - 1);
          }}
        />
      }
      bottomButton={
        <FuturesBotPagination
          step={step}
          onChangeStep={handleStepForm}
          bot={newBotRedux}
          orderStep={newBotRedux.ticker.orderStep}
          priceStepQuery={newBotRedux.ticker.priceStep}
          onChangeInvestmentInBase={handleInvestmentInBase}
          onChangeInvestmentInQuote={handleInvestmentInQuote}
          onChangeOrderList={handleOrderChange}
          showPreview={setShowPreview}
          editing={isEditing}
        />
      }
    >
      <Box>
        <Stack spacing={1}>
          <Stack direction={"row"} spacing={1}>
            <TickerSelect
              tickers={tickers}
              quote={newBotRedux.quote}
              base={newBotRedux.base}
              onChange={handleBase}
            />
            <ButtonStyled
              style={{
                display: "flex",
                maxWidth: 96,
                justifyContent: "center",
                padding: "8px 16px",
              }}
              fullWidth={true}
              onClick={() => handleChartView()}
              color={"secondary"}
              variant="contained"
              endIcon={showChart ? <VisibilityIcon /> : <VisibilityOffIcon />}
            >
              Chart
            </ButtonStyled>
          </Stack>
        </Stack>
        <CollapseChildren show={showChart} collapsedSize={6} hasNextNode>
          <TradingView
            background={theme.palette.background.default}
            headerColor={theme.palette.background.default}
            ChartProperties={ChartOptions}
          />
        </CollapseChildren>

        {step === 1 && (
          <FuturesBotRangeSettings
            bot={newBotRedux}
            onChangeDirection={handleDirection}
            onChangeMinPrice={handleMinPrice}
            onChangeMaxPrice={handleMaxPrice}
            onChangeStartPrice={handleStartPrice}
            onChangePriceStep={handlePriceStep}
            rangeStartMode={rangeStartMode}
            switchRangeStartMode={setRangeStartMode}
            onEdit={setIsEditing}
          />
        )}

        {step === 2 && (
          <FuturesBotGridSettings
            bot={newBotRedux}
            gridsAmount={newBotRedux.gridsAmount}
            gridDirection={newBotRedux.gridDirection}
            gridType={newBotRedux.gridType}
            onChangeGridType={handleGridType}
            onChangeGridsAmount={handleGridsAmount}
            onChangePriceStep={handlePriceStep}
            onChangeInvestmentInBase={handleInvestmentInBase}
            onChangeInvestmentInQuote={handleInvestmentInQuote}
            onChangeOrderList={handleOrderChange}
            priceStep={newBotRedux.priceStep}
            orderStep={newBotRedux.ticker.orderStep}
            orderList={orderList}
            priceStepQuery={newBotRedux.ticker.priceStep}
            rangeStartMode={rangeStartMode}
            showAdvanced={setShowAdvanced}
          />
        )}

        {step === 3 && (
          <FuturesBotInvestmentSettings
            bot={newBotRedux}
            onChangeLeverage={handleLeverage}
            onChangeInvestment={handleInvestment}
            onChangeCurrency={handleCurrencyType}
            tickerPrice={tickerPrice}
            stepInBase={investmentStepInBase}
            stepInQuote={investmentStepInQuote}
            onChangeStepsCount={handleStepsCount}
            priceStepQuery={newBotRedux.ticker.priceStep}
            orderList={orderList}
          />
        )}

        {step === 4 && (
          <InsuranceSettings
            fee={
              `${formatNumber(newBotRedux.investment * 0.12)}
              ${newBotRedux.currencyType === "BASE"
              ? newBotRedux.base
              : newBotRedux.quote}`
            }
          />
        )}
        <Spacer height="110px" />
      </Box>

      {showAdvanced && (
        <AdvancedGridSettings
          showAdvanced={setShowAdvanced}
          orders={orderList}
          bot={newBotRedux}
          stepInBase={investmentStepInBase}
          stepInQuote={investmentStepInQuote}
        />
      )}

      {showPreview && (
        <FuturesBotPreviewPage
          bot={newBotRedux}
          showPreview={setShowPreview}
          stepInBase={investmentStepInBase}
          stepInQuote={investmentStepInQuote}
          stepsCount={newBotRedux.stepsCount}
          tradingViewProperties={ChartOptions}
          orderList={orderList}
        />
      )}
    </PageLayout>
  );
};

export default FuturesBotCreationPage;
